import { Row } from '@tanstack/react-table';

import { Button, ButtonVariation } from '../../../components/Button/Button';
import { ExpandIcon, MoveUserIcon, TrashcanIcon } from '../../../components/Icon';
import type { RosterEntry } from '../types';
import { formatDate, normalizeTestId } from '../../../utils';
import './RosterActionsCell.css';

const showMoveUserAction = false;

export interface PendingRemoval {
  userId: string;
  removalDate: string;
}

interface RosterActionsCellProps {
  row: Row<RosterEntry>;
  onMove?: (entry: RosterEntry) => void;
  onRemove?: (entry: RosterEntry) => void;
  pendingRemoval?: PendingRemoval;
  onCancelRemoval?: (entry: RosterEntry) => void;
  canManageRoster?: boolean;
}

function RosterActionsCell({
  row,
  onMove,
  onRemove,
  pendingRemoval,
  onCancelRemoval,
  canManageRoster,
}: RosterActionsCellProps) {
  const entry = row.original;
  const userName = `${entry.legalFirstName} ${entry.legalLastName}`;
  const baseTestId = 'roster-actions-cell';
  const rowTestId = normalizeTestId(`${baseTestId}-${entry.userId ?? row.index}`);
  const removalDate = pendingRemoval?.removalDate ?? formatDate(entry.scheduledRemovalDate);

  if (canManageRoster && removalDate) {
    return (
      <div
        className='roster-actions-cell roster-actions-cell--pending'
        data-testid={rowTestId}>
        <div
          className='roster-actions-cell__pending-info'
          data-testid={`${rowTestId}-pending`}>
          <span
            className='roster-actions-cell__removal-label'
            data-testid={`${rowTestId}-pending-label`}>
            Date of Removal:
          </span>
          <span
            className='roster-actions-cell__removal-date'
            data-testid={`${rowTestId}-pending-date`}>
            {removalDate}
          </span>
          <Button
            aria-label={`Cancel removal of ${userName}`}
            className='roster-actions-cell__cancel-button'
            data-testid={`${rowTestId}-cancel`}
            variation={ButtonVariation.GHOST}
            onClick={() => onCancelRemoval?.(entry)}>
            Cancel
          </Button>
        </div>
        <button
          aria-expanded={row.getIsExpanded()}
          aria-label={row.getIsExpanded() ? `Collapse details for ${userName}` : `Expand details for ${userName}`}
          className='access-table__expand-button action-icon--blue-square'
          data-testid={`${rowTestId}-expand`}
          type='button'
          onClick={() => row.toggleExpanded()}>
          <ExpandIcon
            expanded={row.getIsExpanded()}
            size={32}
          />
        </button>
      </div>
    );
  }

  return (
    <div
      className='roster-actions-cell'
      data-testid={rowTestId}>
      <button
        aria-expanded={row.getIsExpanded()}
        aria-label={row.getIsExpanded() ? `Collapse details for ${userName}` : `Expand details for ${userName}`}
        className='access-table__expand-button action-icon--blue-square'
        data-testid={`${rowTestId}-expand`}
        type='button'
        onClick={() => row.toggleExpanded()}>
        <ExpandIcon
          expanded={row.getIsExpanded()}
          size={32}
        />
      </button>
      {canManageRoster ? (
        <button
          aria-label={`Remove ${userName} from roster`}
          className='access-table__remove-button action-icon--red-square'
          data-testid={`${rowTestId}-remove`}
          type='button'
          onClick={() => onRemove?.(row.original)}>
          <TrashcanIcon size={32} />
        </button>
      ) : showMoveUserAction ? (
        <button
          aria-label={`Move ${userName} to another roster`}
          className='access-table__move-button action-icon--blue-square'
          data-testid={`${rowTestId}-move`}
          type='button'
          onClick={() => onMove?.(row.original)}>
          <MoveUserIcon size={32} />
        </button>
      ) : null}
    </div>
  );
}

export default RosterActionsCell;
